import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';

// Live ratio compliance — per-classroom present children vs required staff, plus
// recent out-of-ratio incidents. Reads GET /api/ratio/status (live snapshot) and
// GET /api/ratio/breaches (last 30 days). Auto-refreshes every 30s since it's live.

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}
function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

function RoomCard({ room }) {
  const out = !room.in_ratio;
  return (
    <div className={`bg-white rounded-xl border p-5 ${out ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-gray-800">{room.name}</p>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${out ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {out ? `Short ${room.staff_short_by}` : 'In ratio'}
        </span>
      </div>
      <div className="mt-3 flex items-end gap-4">
        <div>
          <p className="text-2xl font-bold text-gray-900">{room.present_staff}<span className="text-gray-400 text-lg">/{room.required_staff}</span></p>
          <p className="text-[11px] text-gray-400">staff present / required</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-2xl font-bold text-gray-900">{room.present_children}</p>
          <p className="text-[11px] text-gray-400">children · 1:{room.max_kids_per_staff}</p>
        </div>
      </div>
    </div>
  );
}

export default function RatioCompliancePage() {
  const [status, setStatus] = useState(null);
  const [breaches, setBreaches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([api.get('/ratio/status'), api.get('/ratio/breaches')]);
      setStatus(s.data || null);
      setBreaches(b.data?.breaches || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load ratio compliance.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const rooms = status?.classrooms || [];
  const outCount = status?.out_of_ratio_count || 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6 animate-slide-up">
      <div className="flex items-center gap-2">
        <UserGroupIcon className="h-7 w-7 text-orange-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ratio Compliance</h1>
          <p className="text-sm text-gray-500">
            Live staff-to-child ratios by classroom.
            {status?.generated_at && <span className="text-gray-400"> Updated {fmtTime(status.generated_at)} · auto-refresh 30s.</span>}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : (
        <>
          <div className={`rounded-xl border p-4 ${outCount > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <p className={`text-sm font-semibold ${outCount > 0 ? 'text-red-700' : 'text-green-700'}`}>
              {outCount > 0
                ? `${outCount} classroom${outCount > 1 ? 's' : ''} out of ratio right now`
                : 'All classrooms in ratio'}
            </p>
            {status?.unassigned_present_staff > 0 && (
              <p className="text-xs text-gray-500 mt-1">{status.unassigned_present_staff} present staff not assigned to a room.</p>
            )}
          </div>

          {rooms.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
              No classrooms with children present right now.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rooms.map((r) => <RoomCard key={r.classroom_id} room={r} />)}
            </div>
          )}

          <div>
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Recent breaches (30 days)</h2>
            {breaches.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
                No out-of-ratio incidents in the last 30 days.
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 px-4 font-medium">Classroom</th>
                      <th className="py-2 px-4 font-medium">Started</th>
                      <th className="py-2 px-4 font-medium">Duration</th>
                      <th className="py-2 px-4 font-medium">Worst short</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breaches.map((b) => (
                      <tr key={b.id} className="border-b border-gray-100">
                        <td className="py-2 px-4 text-gray-800">{b.classroom_name}</td>
                        <td className="py-2 px-4 text-gray-500 whitespace-nowrap">{fmtDateTime(b.started_at)}</td>
                        <td className="py-2 px-4 whitespace-nowrap">
                          {b.ongoing
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">ongoing · {b.minutes_out} min</span>
                            : <span className="text-gray-700">{b.minutes_out} min</span>}
                        </td>
                        <td className="py-2 px-4 text-gray-600">{b.peak_deficit} staff ({b.peak_children} kids)</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
